import React, { useEffect, useMemo, useRef, useState } from "react";
import type {} from "react/jsx-runtime";
type ChatMessage = { role: "app" | "user"; text: string };

type LabelProps = { children: React.ReactNode; htmlFor?: string };
function Label({ children, htmlFor }: LabelProps) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
      {children}
    </label>
  );
}

type CardProps = { children: React.ReactNode };
function Card({ children }: CardProps) {
  return <div className="rounded-3xl border border-slate-200 bg-white/90 backdrop-blur shadow-xl shadow-slate-900/5 p-6">{children}</div>;
}

type ButtonProps = { children: React.ReactNode; onClick?: () => void; disabled?: boolean };
function Button({ children, onClick, disabled }: ButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-xl font-semibold transition ${
        disabled
          ? "bg-slate-200 text-slate-500 cursor-not-allowed"
          : "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

const VoiceKnowledgeApp: React.FC = () => {
  const [apiKey, setApiKey] = useState<string>(() => (typeof window !== "undefined" ? localStorage.getItem("OPENAI_API_KEY") || "" : ""));
  const [sessionActive, setSessionActive] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [, setMouthOpen] = useState<number>(0.2);
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [log, setLog] = useState<ChatMessage[]>([]);
  const [manualInput, setManualInput] = useState<string>("");
  const [useManualInput, setUseManualInput] = useState<boolean>(false);
  const [finalSummary, setFinalSummary] = useState<string>("");
  const [voiceRate, setVoiceRate] = useState<number>(1);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");
  const [micPermission, setMicPermission] = useState<"unknown" | "granted" | "denied">("unknown");

  const mouthIntervalRef = useRef<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const abortListenRef = useRef<(() => void) | null>(null);
  const manualInputRef = useRef<string>("");
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const voiceInitializedRef = useRef<boolean>(false);
  const sessionRunRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioUnlockedRef = useRef<boolean>(false);
  const pendingAudioUnlockRef = useRef<Promise<void> | null>(null);

  const questions = useMemo<string[]>(
    () => [
      "What time do you usually go to bed and wake up on weekdays and weekends?",
      "How long does it typically take you to fall asleep once you're in bed?",
      "Do you use screens (phone, laptop, TV) within an hour of bedtime?",
      "How much caffeine or alcohol do you consume, and at what times?",
      "Describe your sleep environment (noise, light, temperature, bedding).",
      "How often do you exercise, and at what time of day?",
      "What are your top stressors right now and how do you wind down before sleep?",
    ],
    []
  );

  const speak = async (text: string): Promise<void> => {
    return new Promise((resolve) => {
      try {
        if (typeof window === "undefined" || !("speechSynthesis" in window) || typeof (window as any).SpeechSynthesisUtterance === "undefined") {
          setIsSpeaking(false);
          resolve();
          return;
        }
        unlockSpeechAudio();
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = voiceRate;
        const voice = voices.find((v) => v.name === selectedVoice) || voices[0];
        if (voice) utter.voice = voice;
        let fallbackTimer: number | null = null;

        const clearTimers = () => {
          if (fallbackTimer) window.clearTimeout(fallbackTimer);
          fallbackTimer = null;
        };

        const finish = () => {
          setIsSpeaking(false);
          if (mouthIntervalRef.current) window.clearInterval(mouthIntervalRef.current);
          setMouthOpen(0.15);
          clearTimers();
          resolve();
        };

        const scheduleFallback = () => {
          clearTimers();
          const estimated = Math.min(15000, Math.max(2000, text.length * 80));
          fallbackTimer = window.setTimeout(() => {
            finish();
          }, estimated) as unknown as number;
        };

        utter.onstart = () => {
          setIsSpeaking(true);
          if (mouthIntervalRef.current) window.clearInterval(mouthIntervalRef.current);
          mouthIntervalRef.current = window.setInterval(() => {
            setMouthOpen(0.2 + Math.random() * 0.7);
          }, 120) as unknown as number;
          scheduleFallback();
        };
        utter.onend = () => {
          finish();
        };
        utter.onerror = () => {
          finish();
        };
        try {
          window.speechSynthesis.cancel();
          if (typeof window.speechSynthesis.resume === "function") {
            window.speechSynthesis.resume();
          }
        } catch {}
        window.speechSynthesis.speak(utter);
        scheduleFallback();
      } catch {
        resolve();
      }
    });
  };

  const isRecognitionAvailable = useMemo<boolean>(() => {
    if (typeof window === "undefined") return false;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    return Boolean(SR);
  }, []);

  useEffect(() => {
    if (!isRecognitionAvailable) {
      setUseManualInput(true);
      setMicPermission("denied");
    }
  }, [isRecognitionAvailable]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (apiKey) {
        localStorage.setItem("OPENAI_API_KEY", apiKey);
      } else {
        localStorage.removeItem("OPENAI_API_KEY");
      }
    }
  }, [apiKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const synth = window.speechSynthesis;
    const updateVoices = () => {
      const list = synth.getVoices();
      setVoices(list);
      if (list.length && !list.some((v) => v.name === selectedVoice)) {
        const fallback =
          list.find((voice) => voice.name.toLowerCase().includes("google") && voice.lang === "en-US") ||
          list.find((voice) => voice.lang === "en-US") ||
          list[0];
        setSelectedVoice(fallback ? fallback.name : "");
      }
      if (!voiceInitializedRef.current && list.length) {
        voiceInitializedRef.current = true;
        const preferred =
          list.find((voice) => voice.name.toLowerCase().includes("google") && voice.lang === "en-US") ||
          list.find((voice) => voice.lang === "en-US");
        setSelectedVoice((prev) => prev || (preferred ? preferred.name : list[0].name));
      }
    };
    updateVoices();
    if (synth.addEventListener) {
      synth.addEventListener("voiceschanged", updateVoices);
    } else {
      (synth as any).onvoiceschanged = updateVoices;
    }
    return () => {
      if (synth.removeEventListener) {
        synth.removeEventListener("voiceschanged", updateVoices);
      } else {
        (synth as any).onvoiceschanged = null;
      }
    };
  }, [selectedVoice]);

  const listenOnce = async (timeoutMs = 30000): Promise<string> => {
    if (!isRecognitionAvailable) {
      setUseManualInput(true);
      setIsListening(true);
      return new Promise((resolve) => {
        abortListenRef.current = () => {
          setIsListening(false);
          resolve(manualInputRef.current.trim());
        };
      });
    }

    return new Promise((resolve) => {
      const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const rec: any = new SR();
      recognitionRef.current = rec;
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-US";
      rec.maxAlternatives = 1;

      let ended = false;
      let finalTranscript = "";
      let partialTranscript = "";
      let timeoutHandle: number | null = null;
      let restartAttempts = 0;

      const finish = (text: string) => {
        if (ended) return;
        ended = true;
        try {
          rec.stop();
        } catch {}
        setIsListening(false);
        resolve(text.trim());
      };

      const resetTimeout = (delay: number) => {
        if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
        timeoutHandle = window.setTimeout(() => finish(finalTranscript || partialTranscript || ""), delay) as unknown as number;
      };

      setIsListening(true);
      resetTimeout(timeoutMs);

      rec.onresult = (event: any) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = (result[0] && result[0].transcript ? result[0].transcript : "").trim();
          if (!text) continue;
          if (result.isFinal) {
            finalTranscript = finalTranscript ? `${finalTranscript} ${text}` : text;
            partialTranscript = "";
          } else {
            partialTranscript = text;
          }
        }

        if (finalTranscript || partialTranscript) {
          resetTimeout(6000);
        }

        const lastResult = event.results[event.results.length - 1];
        if (lastResult && lastResult.isFinal && finalTranscript) {
          finish(finalTranscript);
        }
      };

      rec.onerror = (event: any) => {
        if (event?.error === "no-speech" && restartAttempts < 2) {
          restartAttempts += 1;
          resetTimeout(2000);
          try {
            rec.stop();
            rec.start();
            return;
          } catch {}
        }
        finish(finalTranscript || partialTranscript || "");
      };

      rec.onend = () => {
        if (!ended) finish(finalTranscript || partialTranscript || "");
      };

      rec.onspeechend = () => {
        resetTimeout(2000);
      };

      abortListenRef.current = () => {
        if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
        finish(finalTranscript || partialTranscript || "");
      };

      try {
        rec.start();
      } catch {
        finish("");
      }
    });
  };

  const ensureMicPermission = async (): Promise<boolean> => {
    if (typeof navigator === "undefined" || !(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setMicPermission("unknown");
      return true;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicPermission("granted");
      return true;
    } catch (err) {
      console.warn("[mic] access denied", err);
      setMicPermission("denied");
      setUseManualInput(true);
      return false;
    }
  };

  const unlockSpeechAudio = async (): Promise<void> => {
    if (typeof window === "undefined") return;
    if (audioUnlockedRef.current) return;
    if (pendingAudioUnlockRef.current) {
      await pendingAudioUnlockRef.current;
      return;
    }
    const unlockPromise = (async () => {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioCtx();
        }
        const ctx = audioContextRef.current;
        if (ctx && ctx.state === "suspended") {
          try {
            await ctx.resume();
          } catch {}
        }
        try {
          if (ctx) {
            const buffer = ctx.createBuffer(1, 1, 22050);
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            source.start();
          }
        } catch {}
      }

      if ("speechSynthesis" in window) {
        try {
          const dummy = new SpeechSynthesisUtterance(" ");
          dummy.volume = 0;
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(dummy);
        } catch {}
      }
      audioUnlockedRef.current = true;
      pendingAudioUnlockRef.current = null;
    })();
    pendingAudioUnlockRef.current = unlockPromise;
    await unlockPromise;
  };

  async function askOpenAI({ system, user }: { system: string; user: string }): Promise<string> {
    if (!apiKey) return "";
    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          temperature: 0.3,
          messages: [
            { role: "system", content: system },
            { role: "user", content: user },
          ],
        }),
      });

      if (!res.ok) {
        console.warn("[askOpenAI] gpt-4o-mini request failed", await res.text());
        return "";
      }

      const data = await res.json();
      const text: string = ((data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) || "").trim();
      return text;
    } catch (err) {
      console.warn("[askOpenAI] gpt-4o-mini threw", err);
      return "";
    }
  }

  const reset = (): number => {
    sessionRunRef.current += 1;
    const runId = sessionRunRef.current;

    if (abortListenRef.current) {
      try {
        abortListenRef.current();
      } catch {}
      abortListenRef.current = null;
    }

    try {
      recognitionRef.current && recognitionRef.current.stop && recognitionRef.current.stop();
    } catch {}
    try {
      window.speechSynthesis.cancel();
    } catch {}

    setIsSpeaking(false);
    setIsListening(false);
    setSessionActive(false);
    setQuestionIndex(0);
    setAnswers([]);
    setLog([]);
    setFinalSummary("");
    setManualInput("");
    manualInputRef.current = "";

    return runId;
  };

  const resumeAudioAndSpeech = async () => {
    await unlockSpeechAudio();
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        if (typeof window.speechSynthesis.resume === "function") {
          window.speechSynthesis.resume();
        }
      }
      if (audioContextRef.current && audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume();
      }
    } catch {}
  };

  const start = async (): Promise<void> => {
    if (!apiKey.trim()) {
      alert("Please enter your OpenAI API key first.");
      return;
    }
    const micOk = await ensureMicPermission();
    if (!micOk) {
      alert("Microphone permission is required for voice capture. Please enable mic access or use manual input.");
      return;
    }
    await resumeAudioAndSpeech();
    const runId = reset();
    setSessionActive(true);
    const intro = "Hi! I'm your sleep coach. I'll ask seven quick questions about your sleep habits. Just answer naturally. Ready?";
    setLog((prev) => (sessionRunRef.current === runId ? [{ role: "app", text: intro }] : prev));
    await speak(intro);
    if (runId !== sessionRunRef.current) return;
    await nextQuestion(0, [], runId);
  };

  const nextQuestion = async (idx: number, currentAnswers: string[], runId: number): Promise<void> => {
    if (runId !== sessionRunRef.current) return;
    if (idx >= questions.length) {
      const qaBlock = questions
        .map((q, i) => `Q${i + 1}: ${q}\nA${i + 1}: ${currentAnswers[i] || "(no answer)"}`)
        .join("\n");
      const prompt = `Create a single paragraph of **exactly ~75 words** that summarizes this person's sleep hygiene and gives 2-3 practical recommendations. Keep it warm, specific, and non-judgmental.\n\n${qaBlock}`;
      const system = `You are a concise, friendly sleep coach. Limit output to one paragraph, ~75 words.`;
      const summary = (await askOpenAI({ system, user: prompt })) || "Here's a concise summary of your sleep patterns with a few tailored tips to try this week.";
      if (runId !== sessionRunRef.current) return;
      setFinalSummary((prev) => (runId !== sessionRunRef.current ? prev : summary));
      setLog((l) => (runId !== sessionRunRef.current ? l : [...l, { role: "app", text: summary }]));
      if (runId !== sessionRunRef.current) return;
      await speak("Thanks! Here is your quick summary and a few tips.");
      if (runId !== sessionRunRef.current) return;
      await speak(summary);
      if (runId !== sessionRunRef.current) return;
      await speak("If you'd like to run this again later, just press restart.");
      if (runId !== sessionRunRef.current) return;
      setSessionActive((prev) => (runId !== sessionRunRef.current ? prev : false));
      return;
    }

    if (runId !== sessionRunRef.current) return;
    const question = questions[idx];
    setLog((l) => (runId !== sessionRunRef.current ? l : [...l, { role: "app", text: question }]));
    if (runId !== sessionRunRef.current) return;
    await speak(question);
    if (runId !== sessionRunRef.current) return;

    const heard = (await listenOnce()) || "";
    const cleaned = heard.trim();

    if (runId !== sessionRunRef.current) return;
    if (cleaned) {
      setLog((l) => (runId !== sessionRunRef.current ? l : [...l, { role: "user", text: cleaned }]));
    } else {
      setLog((l) => (runId !== sessionRunRef.current ? l : [...l, { role: "user", text: "(no audible response)" }]));
    }

    if (runId !== sessionRunRef.current) return;
    const newAnswers = [...currentAnswers, cleaned];
    setAnswers((prev) => (runId !== sessionRunRef.current ? prev : newAnswers));

    const ackSystem = "You are a brief, empathetic sleep coach. Respond in one short sentence (<=18 words). No new questions.";
    const ackUser = `Question: ${question}\nAnswer: ${cleaned || "(no answer)"}\nWrite a one-sentence acknowledgment.`;
    const ack = (await askOpenAI({ system: ackSystem, user: ackUser })) || "Got it.";

    if (runId !== sessionRunRef.current) return;
    setLog((l) => (runId !== sessionRunRef.current ? l : [...l, { role: "app", text: ack }]));
    await speak(ack);

    if (runId !== sessionRunRef.current) return;
    setQuestionIndex((prev) => (runId !== sessionRunRef.current ? prev : idx + 1));
    await nextQuestion(idx + 1, newAnswers, runId);
  };

  useEffect(() => {
    return () => {
      if (mouthIntervalRef.current) window.clearInterval(mouthIntervalRef.current);
      try {
        window.speechSynthesis.cancel();
      } catch {}
      try {
        recognitionRef.current && recognitionRef.current.stop && recognitionRef.current.stop();
      } catch {}
      try {
        audioContextRef.current && audioContextRef.current.close && audioContextRef.current.close();
      } catch {}
    };
  }, []);

  // Avatar component using GIFs for speaking and listening
  const Avatar: React.FC = () => {
    let avatarSrc = "/idle.gif";
    let statusLabel = "Idle";
    if (isSpeaking) {
      avatarSrc = "/speaking.gif";
      statusLabel = "Speaking";
    } else if (isListening) {
      avatarSrc = "/listening.gif";
      statusLabel = "Listening";
    } else if (sessionActive) {
      avatarSrc = "/idle.gif";
      statusLabel = "Ready";
    }
    return (
      <div className="relative w-40 h-40 mx-auto sm:w-48 sm:h-48">
        <div className={`absolute inset-0 rounded-full ${isListening ? "ring-4 ring-sky-300/70 animate-pulse" : "ring-2 ring-slate-200"}`} />
        <img src={avatarSrc} alt={statusLabel + " avatar"} className="absolute inset-0 w-full h-full object-contain rounded-full" />
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-xs px-3 py-1 rounded-full bg-white text-slate-900 shadow">
          {statusLabel}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const runSelfTests = () => {
      const qs = ["q1", "q2"]; 
      const ans = ["a1", "a2"]; 
      const block = qs
        .map((q, i) => `Q${i + 1}: ${q}\nA${i + 1}: ${ans[i] || "(no answer)"}`)
        .join("\n");
      const expected = "Q1: q1\nA1: a1\nQ2: q2\nA2: a2";
      console.assert(block === expected, "qaBlock format mismatch", { block, expected });
      const blockMissing = qs
        .map((q, i) => `Q${i + 1}: ${q}\nA${i + 1}: ${(i === 1 ? "" : ans[i]) || "(no answer)"}`)
        .join("\n");
      console.assert(blockMissing.includes("(no answer)"), "qaBlock should include '(no answer)' when missing");
    };
    try { runSelfTests(); } catch {}
  }, []);

  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [log, finalSummary]);

  const friendlyStatus = sessionActive ? (isSpeaking ? "Speaking..." : isListening ? "Listening..." : "Coach ready") : "Waiting to start";
  const questionProgress = Math.min(questionIndex + 1, questions.length);
  const answeredCount = answers.filter((a) => Boolean(a && a.trim())).length;
  const modeLabel = useManualInput ? "Manual fallback" : isRecognitionAvailable ? "Voice first" : "Manual only";
  const micStatusText =
    micPermission === "granted" ? "Microphone ready" : micPermission === "denied" ? "Microphone blocked or unavailable" : "Microphone permission pending";

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-white to-slate-100 text-slate-900">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8 space-y-3 text-center sm:text-left">
          <p className="text-[11px] uppercase tracking-[0.35em] text-slate-500">Sleep Studio</p>
          <h1 className="text-4xl font-semibold sm:text-5xl">Tom's Sleep Coach</h1>
          <p className="mx-auto max-w-3xl text-sm text-slate-600 sm:text-base">
            A calm AI companion that interviews you about your evening rituals, listens for each response, and then crafts a concise plan to
            help you wind down smarter.
          </p>
        </header>

        <div className="mb-10 grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Current question</div>
            <div className="mt-1 text-3xl font-semibold">{sessionActive ? questionProgress : "0"}</div>
            <p className="text-sm text-slate-500">of {questions.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Conversation turns</div>
            <div className="mt-1 text-3xl font-semibold">{log.length}</div>
            <p className="text-sm text-slate-500">{sessionActive ? "Live session" : finalSummary ? "Session complete" : "Standing by"}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Interaction mode</div>
            <div className="mt-1 text-3xl font-semibold">{modeLabel}</div>
            <p className="text-sm text-slate-500">{isRecognitionAvailable ? "Speech recognition available" : "Browser fallback active"}</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[360px,1fr]">
          <Card>
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-4 text-center">
                <Avatar />
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Realtime coach</p>
                  <p className="text-2xl font-semibold text-slate-900">{friendlyStatus}</p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold">
                  <span
                    className={`rounded-full border px-3 py-1 ${isListening ? "border-sky-300 bg-sky-50 text-sky-600" : "border-slate-200 text-slate-500"}`}
                  >
                    {isListening ? "Mic live" : "Mic idle"}
                  </span>
                  <span
                    className={`rounded-full border px-3 py-1 ${isSpeaking ? "border-emerald-300 bg-emerald-50 text-emerald-600" : "border-slate-200 text-slate-500"}`}
                  >
                    {isSpeaking ? "Voice playing" : "Voice paused"}
                  </span>
                  <span className="rounded-full border border-slate-200 px-3 py-1 text-slate-500">{sessionActive ? "Session active" : "Ready"}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                  <div>
                    <Label htmlFor="apiKeyInput">OpenAI API key</Label>
                    <input
                      id="apiKeyInput"
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-..."
                      className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
                    />
                    <p className="text-xs text-slate-500 mt-1">Stored in your browser (localStorage). Never hardcode keys in production.</p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <Label htmlFor="manual">Manual input fallback</Label>
                      <p className="text-[11px] text-slate-500 normal-case">Use typing whenever speech capture is unavailable.</p>
                    </div>
                    <input
                      id="manual"
                      type="checkbox"
                      checked={useManualInput}
                      onChange={(e) => setUseManualInput(e.target.checked)}
                      className="h-5 w-5 accent-sky-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                    <span>{micStatusText}</span>
                    {micPermission !== "granted" && (
                      <button
                        type="button"
                        className="font-semibold text-sky-600 hover:text-sky-800"
                        onClick={() => {
                          void ensureMicPermission();
                        }}
                      >
                        Request mic access
                      </button>
                    )}
                  </div>
                  <div>
                    <Label>Voice rate</Label>
                    <input
                      type="range"
                      min={0.8}
                      max={1.3}
                      step={0.05}
                      value={voiceRate}
                      onChange={(e) => setVoiceRate(parseFloat(e.target.value))}
                      className="w-full accent-sky-500"
                    />
                    <div className="mt-1 text-[11px] text-slate-500">Fine tune how quickly the avatar reads each answer.</div>
                  </div>
                  <div>
                    <Label htmlFor="voiceSelect">Voice</Label>
                    <select
                      id="voiceSelect"
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      disabled={!voices.length}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                    >
                      {!voices.length && <option>Loading voices...</option>}
                      {voices.map((voice) => (
                        <option key={voice.name} value={voice.name}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {useManualInput && isListening && (
                  <div className="rounded-2xl border border-amber-300 bg-amber-50/70 p-4">
                    <Label htmlFor="manualIn">Type your answer</Label>
                    <input
                      id="manualIn"
                      value={manualInput}
                      onChange={(e) => {
                        setManualInput(e.target.value);
                        manualInputRef.current = e.target.value;
                      }}
                      placeholder={'Type here, then click "Submit answer"'}
                      className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        onClick={() => {
                          if (abortListenRef.current) abortListenRef.current();
                          setManualInput("");
                          manualInputRef.current = "";
                        }}
                      >
                        Submit answer
                      </Button>
                      <Button
                        onClick={() => {
                          setManualInput("");
                          manualInputRef.current = "";
                          if (abortListenRef.current) abortListenRef.current();
                        }}
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {!sessionActive ? (
                    <Button
                      onClick={async () => {
                        await resumeAudioAndSpeech();
                        void start();
                      }}
                      disabled={!apiKey.trim()}
                    >
                      Start coaching
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={async () => {
                          await resumeAudioAndSpeech();
                          reset();
                        }}
                      >
                        Stop session
                      </Button>
                      <Button
                        onClick={async () => {
                          await resumeAudioAndSpeech();
                          void start();
                        }}
                      >
                        Restart
                      </Button>
                    </>
                  )}
                  {!sessionActive && (
                    <button
                      type="button"
                      onClick={async () => {
                        await resumeAudioAndSpeech();
                        reset();
                      }}
                      className="text-sm font-semibold text-slate-500 transition hover:text-slate-900"
                    >
                      Clear conversation
                    </button>
                  )}
                  {/* Manual test voice button for mobile audio unlock */}
                  <Button
                    onClick={async () => {
                      await resumeAudioAndSpeech();
                      await speak("Testing voice. If you hear this, voice is unlocked.");
                    }}
                  >
                    Test voice
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Live transcript</p>
                  <h2 className="text-2xl font-semibold text-slate-900">Conversation</h2>
                </div>
                <div className="text-sm text-slate-500">
                  {sessionActive ? `Listening for question ${questionProgress}...` : finalSummary ? "Session complete" : "Press start to begin"}
                </div>
              </div>

              <div
                ref={conversationRef}
                className="h-[320px] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3 sm:h-[420px] lg:h-[480px]"
              >
                {log.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center text-sm text-slate-500">
                    Responses will appear here once the interview begins.
                  </div>
                ) : (
                  log.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "app" ? "justify-start" : "justify-end"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl border px-4 py-3 text-sm shadow ${
                          m.role === "app" ? "border-slate-200 bg-white text-slate-800" : "border-sky-200 bg-sky-50 text-slate-900"
                        }`}
                      >
                        <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">{m.role === "app" ? "Coach" : "You"}</div>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                  ))
                )}
                {finalSummary && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <div className="mb-1 text-[11px] uppercase tracking-wide">Summary</div>
                    <div className="whitespace-pre-wrap">{finalSummary}</div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900">Answer capture</span>
                  <span className="text-xs text-slate-500">
                    {answeredCount}/{questions.length}
                  </span>
                </div>
                <div className="mt-3 max-h-48 space-y-3 overflow-y-auto pr-1 sm:pr-2">
                  {questions.map((q, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">Q{i + 1}</div>
                      <div className="text-sm font-medium text-slate-900">{q}</div>
                      <div className="text-sm text-slate-600">{answers[i] ? answers[i] : <span className="italic text-slate-400">(no answer)</span>}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>

        <footer className="mt-10 text-center text-xs text-slate-500">
          Tip: Chrome desktop gives the clearest voice capture. Your API key never leaves this browser demo.
        </footer>
      </div>
    </div>
  );
}

export default VoiceKnowledgeApp;
